import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, FolderOpen, MessageSquare, Activity,
  Plus, Link2, Send, Edit2, Trash2, Check, Clock, 
  UserPlus, MoreVertical, Image, Calendar, MapPin
} from 'lucide-react';

const StudioProjectDetail = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [studioId, setStudioId] = useState(null);
  const [assignedMembers, setAssignedMembers] = useState([]);
  const [boards, setBoards] = useState([]);
  const [activities, setActivities] = useState([]);
  const [comments, setComments] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showAddMember, setShowAddMember] = useState(false);
  const [showLinkBoard, setShowLinkBoard] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  
  // Form states
  const [newComment, setNewComment] = useState('');
  const [availableMembers, setAvailableMembers] = useState([]);
  const [userBoards, setUserBoards] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [editForm, setEditForm] = useState({});

  const fetchProjectData = useCallback(async () => {
    try {
      // First get studio info
      const studioRes = await axios.get(`${API}/studios/my-studio`, { withCredentials: true });
      if (!studioRes.data.studio) {
        navigate('/studio');
        return;
      }
      
      const sid = studioRes.data.studio.studio_id;
      setStudioId(sid);
      setPermissions(studioRes.data.permissions || {});
      
      // Get project detail
      const projectRes = await axios.get(`${API}/studios/${sid}/projects/${projectId}`, { withCredentials: true });
      setProject(projectRes.data.project);
      setAssignedMembers(projectRes.data.assigned_members || []);
      setBoards(projectRes.data.boards || []);
      setActivities(projectRes.data.activities || []);
      setComments(projectRes.data.comments || []);
      setEditForm(projectRes.data.project);
      
      // Get studio members for assignment
      const membersRes = await axios.get(`${API}/studios/${sid}/members`, { withCredentials: true });
      const allMembers = [
        { user_id: membersRes.data.owner.user_id, user_name: membersRes.data.owner.name, role: 'owner' },
        ...membersRes.data.members.map(m => ({ user_id: m.user_id, user_name: m.user_name, role: m.role }))
      ];
      // Filter out already assigned
      const assignedIds = new Set(projectRes.data.assigned_members.map(m => m.user_id));
      setAvailableMembers(allMembers.filter(m => !assignedIds.has(m.user_id)));
      
      // Get user's boards
      const boardsRes = await axios.get(`${API}/boards`, { withCredentials: true });
      setUserBoards(boardsRes.data || []);
      
    } catch (err) {
      console.error('Error fetching project:', err);
      toast.error('Errore nel caricamento del progetto');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  const handleAssignMember = async () => {
    if (!selectedMemberId) return;
    
    try {
      await axios.post(`${API}/studios/${studioId}/projects/${projectId}/assign`, {
        user_id: selectedMemberId,
        role: 'contributor'
      }, { withCredentials: true });
      
      toast.success('Membro assegnato al progetto');
      setShowAddMember(false);
      setSelectedMemberId('');
      fetchProjectData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'assegnazione');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Rimuovere questo membro dal progetto?')) return;
    
    try {
      await axios.delete(`${API}/studios/${studioId}/projects/${projectId}/members/${userId}`, { withCredentials: true });
      toast.success('Membro rimosso');
      fetchProjectData();
    } catch (err) {
      toast.error('Errore nella rimozione');
    }
  };

  const handleLinkBoard = async () => {
    if (!selectedBoardId) return;
    
    try {
      await axios.post(`${API}/studios/${studioId}/projects/${projectId}/link-board`, {
        board_id: selectedBoardId
      }, { withCredentials: true });
      
      toast.success('Board collegata al progetto');
      setShowLinkBoard(false);
      setSelectedBoardId('');
      fetchProjectData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel collegamento');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(`${API}/studios/${studioId}/projects/${projectId}/comments`, {
        content: newComment,
        target_type: 'project',
        target_id: projectId,
        is_internal: true,
        mentions: []
      }, { withCredentials: true });
      
      toast.success('Commento aggiunto');
      setNewComment('');
      fetchProjectData();
    } catch (err) {
      toast.error('Errore nell\'aggiunta del commento');
    }
  };

  const handleUpdateProject = async () => {
    try {
      await axios.put(`${API}/studios/${studioId}/projects/${projectId}`, editForm, { withCredentials: true });
      toast.success('Progetto aggiornato');
      setShowEditProject(false);
      fetchProjectData();
    } catch (err) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/studios/${studioId}/projects/${projectId}`, {
        status: newStatus
      }, { withCredentials: true });
      toast.success('Stato aggiornato');
      fetchProjectData();
    } catch (err) {
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'draft': 'bg-gray-100 text-gray-700',
      'active': 'bg-green-100 text-green-700',
      'on_hold': 'bg-yellow-100 text-yellow-700',
      'completed': 'bg-[var(--gold)]/20 text-[var(--gold)]',
      'archived': 'bg-gray-100 text-gray-500'
    };
    const names = {
      'draft': 'Bozza',
      'active': 'Attivo',
      'on_hold': 'In Pausa',
      'completed': 'Completato',
      'archived': 'Archiviato'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.draft}`}>
        {names[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">Progetto non trovato</p>
          <Button onClick={() => navigate('/studio')} className="mt-4">
            Torna allo Studio
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <button 
          onClick={() => navigate('/studio')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna allo Studio
        </button>

        {/* Project Header */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold" data-testid="project-title">{project.name}</h1>
                {getStatusBadge(project.status)}
              </div>
              
              {project.description && (
                <p className="text-muted-foreground mb-4">{project.description}</p>
              )}
              
              <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
                {project.client_name && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>Cliente: {project.client_name}</span>
                  </div>
                )}
                {project.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{project.location}</span>
                  </div>
                )}
                {project.deadline && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Scadenza: {formatDate(project.deadline)}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Creato: {formatDate(project.created_at)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              {permissions.edit_projects && (
                <Button variant="outline" size="sm" onClick={() => setShowEditProject(true)}>
                  <Edit2 className="w-4 h-4 mr-1" />
                  Modifica
                </Button>
              )}
              {permissions.manage_projects && (
                <select
                  value={project.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="border rounded-lg px-3 py-1 text-sm"
                >
                  <option value="draft">Bozza</option>
                  <option value="active">Attivo</option>
                  <option value="on_hold">In Pausa</option>
                  <option value="completed">Completato</option>
                  <option value="archived">Archiviato</option>
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b overflow-x-auto">
          {['overview', 'boards', 'team', 'activity', 'comments'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === 'overview' && 'Panoramica'}
              {tab === 'boards' && `Board (${boards.length})`}
              {tab === 'team' && `Team (${assignedMembers.length})`}
              {tab === 'activity' && 'Attività'}
              {tab === 'comments' && `Commenti (${comments.length})`}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-primary" />
                Board Collegate
              </h3>
              <p className="text-3xl font-bold">{boards.length}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowLinkBoard(true)}>
                <Link2 className="w-4 h-4 mr-1" />
                Collega Board
              </Button>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Team Assegnato
              </h3>
              <p className="text-3xl font-bold">{assignedMembers.length}</p>
              {permissions.manage_projects && (
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowAddMember(true)}>
                  <UserPlus className="w-4 h-4 mr-1" />
                  Assegna Membro
                </Button>
              )}
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Attività Recenti
              </h3>
              <p className="text-3xl font-bold">{activities.length}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {activities[0] ? `Ultima: ${formatDate(activities[0].created_at)}` : 'Nessuna attività'}
              </p>
            </div>

            {/* Recent Activity Preview */}
            <div className="bg-white rounded-xl p-5 shadow-sm md:col-span-2 lg:col-span-3">
              <h3 className="font-semibold mb-4">Attività Recenti</h3>
              {activities.slice(0, 5).map((activity) => (
                <div key={activity.activity_id} className="flex items-start gap-3 py-3 border-b last:border-0">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Activity className="w-4 h-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm">
                      <span className="font-medium">{activity.user_name}</span> {activity.description}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDate(activity.created_at)}</p>
                  </div>
                </div>
              ))}
              {activities.length === 0 && (
                <p className="text-muted-foreground text-center py-4">Nessuna attività ancora</p>
              )}
            </div>
          </div>
        )}

        {/* Boards Tab */}
        {activeTab === 'boards' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Board del Progetto</h2>
              <Button onClick={() => setShowLinkBoard(true)}>
                <Link2 className="w-4 h-4 mr-2" />
                Collega Board
              </Button>
            </div>
            
            {boards.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Nessuna board collegata</p>
                <Button variant="outline" onClick={() => setShowLinkBoard(true)}>
                  Collega una Board
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {boards.map((board) => (
                  <div 
                    key={board.board_id}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/projects/${board.board_id}`)}
                  >
                    <div className="aspect-video bg-gray-100">
                      {board.thumbnail ? (
                        <img src={board.thumbnail} alt={board.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-10 h-10 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold">{board.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {board.items?.length || 0} materiali
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Team Assegnato</h2>
              {permissions.manage_projects && availableMembers.length > 0 && (
                <Button onClick={() => setShowAddMember(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assegna Membro
                </Button>
              )}
            </div>
            
            {assignedMembers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Nessun membro assegnato</p>
                {permissions.manage_projects && (
                  <Button variant="outline" onClick={() => setShowAddMember(true)}>
                    Assegna un Membro
                  </Button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {assignedMembers.map((member) => (
                  <div key={member.user_id} className="p-4 border-b last:border-0 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        {member.user_avatar ? (
                          <img src={member.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <Users className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{member.user_name || member.user_email}</p>
                        <p className="text-sm text-muted-foreground capitalize">{member.role}</p>
                      </div>
                    </div>
                    {permissions.manage_projects && (
                      <button 
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="text-red-500 hover:bg-red-50 p-2 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="bg-white rounded-xl shadow-sm">
            {activities.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessuna attività registrata</p>
              </div>
            ) : (
              activities.map((activity, idx) => (
                <div 
                  key={activity.activity_id} 
                  className={`p-4 flex items-start gap-3 ${idx !== activities.length - 1 ? 'border-b' : ''}`}
                >
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Activity className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">
                      <span className="font-medium">{activity.user_name}</span> {activity.description}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(activity.created_at).toLocaleString('it-IT')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div>
            {/* Add Comment */}
            <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Scrivi un commento sul progetto..."
                    className="w-full border rounded-lg p-3 resize-none h-20"
                    data-testid="project-comment-input"
                  />
                  <div className="flex justify-end mt-2">
                    <Button onClick={handleAddComment} disabled={!newComment.trim()} data-testid="add-comment-btn">
                      <Send className="w-4 h-4 mr-2" />
                      Invia
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Comments List */}
            <div className="bg-white rounded-xl shadow-sm">
              {comments.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nessun commento ancora. Inizia la discussione!</p>
                </div>
              ) : (
                comments.map((comment, idx) => (
                  <div 
                    key={comment.comment_id} 
                    className={`p-4 ${idx !== comments.length - 1 ? 'border-b' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        {comment.user_avatar ? (
                          <img src={comment.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <MessageSquare className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{comment.user_name || 'Utente'}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.created_at).toLocaleString('it-IT')}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Assign Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Assegna Membro al Progetto</h2>
            {availableMembers.length === 0 ? (
              <p className="text-muted-foreground mb-4">Tutti i membri dello studio sono già assegnati a questo progetto.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Seleziona Membro</label>
                  <select
                    value={selectedMemberId}
                    onChange={(e) => setSelectedMemberId(e.target.value)}
                    className="w-full border rounded-lg p-2"
                    data-testid="select-member"
                  >
                    <option value="">Seleziona...</option>
                    {availableMembers.map((member) => (
                      <option key={member.user_id} value={member.user_id}>
                        {member.user_name} ({member.role})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowAddMember(false)}>
                Annulla
              </Button>
              {availableMembers.length > 0 && (
                <Button onClick={handleAssignMember} disabled={!selectedMemberId}>
                  Assegna
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Link Board Modal */}
      {showLinkBoard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Collega Board al Progetto</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Seleziona Board</label>
                <select
                  value={selectedBoardId}
                  onChange={(e) => setSelectedBoardId(e.target.value)}
                  className="w-full border rounded-lg p-2"
                  data-testid="select-board"
                >
                  <option value="">Seleziona...</option>
                  {userBoards.map((board) => (
                    <option key={board.board_id} value={board.board_id}>
                      {board.project_name || board.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowLinkBoard(false)}>
                Annulla
              </Button>
              <Button onClick={handleLinkBoard} disabled={!selectedBoardId}>
                Collega
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Modifica Progetto</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome Progetto</label>
                <Input
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione</label>
                <Input
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome Cliente</label>
                  <Input
                    value={editForm.client_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email Cliente</label>
                  <Input
                    type="email"
                    value={editForm.client_email || ''}
                    onChange={(e) => setEditForm({ ...editForm, client_email: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <Input
                  value={editForm.location || ''}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowEditProject(false)}>
                Annulla
              </Button>
              <Button onClick={handleUpdateProject}>
                Salva Modifiche
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StudioProjectDetail;
